import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { CustomSentenceApi, ICustomSentence } from '../api/CustomSentenceApi';
import { AudioApi } from '../api/AudioApi';
import { ChineseTeacher } from '../api/UserApi';
import { SentenceApi } from '../api/SentenceApi';
import { ActivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import { LS_PUBLIC_SCORE_IMPORT_ID } from '../referrerStorage';
import { BLUE, BORDER, ERROR_SOFT, ERROR_TEXT, INTER, NAVY, POPPINS, SLATE, SOFT_BLUE, SURFACE_MUTED, YELLOW } from '../style/verbaScholaPalette';
import { pronunciationCardSx, pronunciationButtonSx, pronunciationError } from './studentPronunciationStyles';
import StudentPronunciationPractice from './StudentPronunciationPractice';

const queueChipSx = { color: NAVY, backgroundColor: `${YELLOW}55`, fontFamily: INTER, fontWeight: 700 };

function sentenceIsPlayable(sentence: ICustomSentence, teacher: ChineseTeacher): boolean {
    // Older API responses do not have per-voice readiness.
    return sentence.playable_teacher_voices !== undefined
        ? sentence.playable_teacher_voices.includes(teacher)
        : !sentence.processing_error && (sentence.processing_queue ?? 0) <= 0;
}

function sentenceGroup(sentence: ICustomSentence): string {
    if (!sentence.latest_chat_date) return 'Older';
    const date = new Date(sentence.latest_chat_date).toDateString();
    const today = new Date();
    if (date === today.toDateString()) return 'Today';
    today.setDate(today.getDate() - 1);
    return date === today.toDateString() ? 'Yesterday' : 'Older';
}

/** Live data and actions with the responsive layout of the student pronunciation mockup. */
export default function StudentPronunciationWorkspace() {
    useUserActivityTracker(ActivityEnum.CustomSentence);
    const history = useHistory();
    const location = useLocation();
    const [sentences, setSentences] = React.useState<ICustomSentence[]>([]);
    const [selectedId, setSelectedId] = React.useState<number | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [audioRequestError, setAudioRequestError] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [addOpen, setAddOpen] = React.useState(false);
    const [draft, setDraft] = React.useState('');
    const [addError, setAddError] = React.useState('');
    const [adding, setAdding] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [deleteSentence, setDeleteSentence] = React.useState<ICustomSentence | null>(null);
    const [deleteError, setDeleteError] = React.useState('');
    const [summaries, setSummaries] = React.useState<Record<number, { count: number; score: number | null }>>({});
    const [audioRequestRetry, setAudioRequestRetry] = React.useState(0);
    const [teacher, setTeacher] = React.useState(() => AudioApi.selectedCustomSentenceTeacher());
    const [playbackSettingsRefreshing, setPlaybackSettingsRefreshing] = React.useState(false);
    const alive = React.useRef(false);
    const listVersion = React.useRef(0);
    const requestedAudio = React.useRef(new Set<string>());
    const audioRequestsInFlight = React.useRef(new Set<string>());
    const explicitAudioRetries = React.useRef(new Set<string>());
    const requestAfterSettingsChange = React.useRef<string | null>(null);
    const selectedRequest = React.useRef({ id: selectedId, teacher });
    selectedRequest.current = { id: selectedId, teacher };
    const selected = sentences.find((sentence) => sentence.id === selectedId);
    const selectedReady = selected ? !playbackSettingsRefreshing && sentenceIsPlayable(selected, teacher) : false;
    const selectedAudioDelayed = !selectedReady && selected?.audio_request_status?.[teacher]?.state === 'retrying';
    // A sentence-wide backlog error does not stop this student's queued voice.
    const selectedAudioFailed = Boolean(selected?.processing_error) && !selected?.audio_request_status?.[teacher];
    const queuedSentences = sentences.filter((sentence) => !sentenceIsPlayable(sentence, teacher) &&
        (!sentence.processing_error || Boolean(sentence.audio_request_status?.[teacher])) &&
        ((sentence.processing_queue ?? 0) > 0 || Boolean(sentence.audio_request_status?.[teacher])));
    const pollInterval = (selected && !selectedReady && !selectedAudioFailed && !selectedAudioDelayed && !audioRequestError)
        || queuedSentences.some((sentence) => sentence.processing_queue === 1 &&
            sentence.audio_request_status?.[teacher]?.state !== 'retrying')
        ? 5000 : queuedSentences.length > 0 ? 30000 : null;

    const refresh = React.useCallback(async () => {
        const version = ++listVersion.current;
        try {
            const response = await CustomSentenceApi.getAll();
            if (!alive.current || version !== listVersion.current) return;
            const list = [...response.data].sort(
                (a, b) => (b.latest_chat_date || '').localeCompare(a.latest_chat_date || '') || b.id - a.id,
            );
            setSentences(list);
            setSelectedId((current) => (list.some((sentence) => sentence.id === current) ? current : (list[0]?.id ?? null)));
            setError('');
            setPlaybackSettingsRefreshing(false);
        } catch (err) {
            if (alive.current && version === listVersion.current) setError(pronunciationError(err));
        } finally {
            if (alive.current && version === listVersion.current) setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        alive.current = true;
        void refresh();
        return () => {
            alive.current = false;
            listVersion.current += 1;
        };
    }, [refresh]);

    React.useEffect(() => {
        return AudioApi.subscribeCustomSentenceTeacher(() => {
            const nextTeacher = AudioApi.selectedCustomSentenceTeacher();
            setPlaybackSettingsRefreshing(true);
            setAudioRequestError('');
            requestedAudio.current.clear();
            requestAfterSettingsChange.current = selectedRequest.current.id === null ? null : `${selectedRequest.current.id}:${nextTeacher}`;
            setTeacher(nextTeacher);
            void refresh();
        });
    }, [refresh]);

    React.useEffect(() => {
        setAudioRequestError('');
    }, [selectedId, teacher]);

    React.useEffect(() => {
        if (!selected || selectedReady || playbackSettingsRefreshing) return;
        const key = `${selected.id}:${teacher}`;
        const retry = explicitAudioRetries.current.has(key);
        const changedSettings = requestAfterSettingsChange.current === key;
        if (!retry && !changedSettings && (selected.processing_error || selected.audio_request_status?.[teacher])) return;
        if (requestedAudio.current.has(key) || audioRequestsInFlight.current.has(key)) return;
        explicitAudioRetries.current.delete(key);
        if (changedSettings) requestAfterSettingsChange.current = null;
        requestedAudio.current.add(key);
        audioRequestsInFlight.current.add(key);
        void CustomSentenceApi.requestAudio(selected.id, teacher, retry)
            .then(() => {
                if (!alive.current) return;
                if (selectedRequest.current.id === selected.id && selectedRequest.current.teacher === teacher) {
                    setAudioRequestError('');
                }
                // A PUT response may be older than an in-flight list refresh.
                void refresh();
            })
            .catch((err) => {
                requestedAudio.current.delete(key);
                if (alive.current && selectedRequest.current.id === selected.id && selectedRequest.current.teacher === teacher) {
                    setAudioRequestError(pronunciationError(err));
                }
            })
            .finally(() => {
                audioRequestsInFlight.current.delete(key);
                if (alive.current && requestAfterSettingsChange.current === key) setAudioRequestRetry(value => value + 1);
            });
    }, [selected?.id, selected?.processing_error, selected?.audio_request_status?.[teacher]?.state,
        selectedReady, teacher, audioRequestRetry, playbackSettingsRefreshing, refresh]);

    React.useEffect(() => {
        if (pollInterval === null) return;
        let stopped = false;
        let timer: ReturnType<typeof setTimeout>;
        const poll = async () => {
            await refresh();
            if (!stopped) timer = setTimeout(poll, pollInterval);
        };
        timer = setTimeout(poll, pollInterval);
        return () => {
            stopped = true;
            clearTimeout(timer);
        };
    }, [pollInterval, refresh]);

    React.useEffect(() => {
        const text = new URLSearchParams(location.search).get('text');
        if (text) {
            setDraft(text);
            setAddOpen(true);
        }
    }, [location.search]);

    React.useEffect(() => {
        const publicId = localStorage.getItem(LS_PUBLIC_SCORE_IMPORT_ID);
        if (!publicId) return;
        localStorage.removeItem(LS_PUBLIC_SCORE_IMPORT_ID);
        void CustomSentenceApi.createPublic(publicId)
            .then((response) => {
                if (!alive.current) return;
                setSelectedId(response.data.id);
                void refresh();
            })
            .catch((err) => {
                if (alive.current) setError(pronunciationError(err));
            });
    }, [refresh]);

    const openAdd = () => {
        setDraft('');
        setAddError('');
        setAddOpen(true);
    };
    const closeAdd = () => {
        if (adding) return;
        setAddOpen(false);
        if (location.search) history.replace(location.pathname);
    };
    const addSentence = async (event: React.FormEvent) => {
        event.preventDefault();
        const text = draft.trim();
        if (!text) {
            setAddError('Enter a Chinese sentence first.');
            return;
        }
        if (Array.from(text).length > 70) {
            setAddError('Use 70 characters or fewer.');
            return;
        }
        setAdding(true);
        setAddError('');
        try {
            // Generate pinyin for the exact submitted text, using the server's pronunciation rules.
            const pinyin = (await SentenceApi.getTextPinyin(text)).data.flat();
            const response = await CustomSentenceApi.create({ id: 0, text, pinyin, teacher_voice: teacher });
            if (!alive.current) return;
            listVersion.current += 1;
            const created = { ...response.data, latest_chat_date: new Date().toISOString() };
            requestedAudio.current.add(`${created.id}:${teacher}`);
            setSentences((current) => [created, ...current.filter((sentence) => sentence.id !== created.id)]);
            setSelectedId(created.id);
            setAddOpen(false);
            setDraft('');
            if (location.search) history.replace(location.pathname);
            void refresh();
        } catch (err) {
            if (alive.current) setAddError(pronunciationError(err));
        } finally {
            if (alive.current) setAdding(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteSentence) return;
        setDeleteError('');
        setDeleting(true);
        try {
            await CustomSentenceApi.deleteBySentenceId(deleteSentence.id);
            if (!alive.current) return;
            listVersion.current += 1;
            setSentences((current) => current.filter((sentence) => sentence.id !== deleteSentence.id));
            setDeleteSentence(null);
            await refresh();
        } catch (err) {
            if (alive.current) setDeleteError(pronunciationError(err));
        } finally {
            if (alive.current) setDeleting(false);
        }
    };

    const updateSummary = React.useCallback((id: number, count: number, score: number | null) => {
        setSummaries((current) => ({ ...current, [id]: { count, score } }));
    }, []);

    const retrySelectedAudio = (forceAudio = false) => {
        if (selected && !selectedReady && !playbackSettingsRefreshing && (forceAudio || audioRequestError || selectedAudioDelayed)) {
            const key = `${selected.id}:${teacher}`;
            if (audioRequestsInFlight.current.has(key)) return;
            explicitAudioRetries.current.add(key);
            requestedAudio.current.delete(key);
            setAudioRequestError('');
            setAudioRequestRetry((value) => value + 1);
        } else {
            void refresh();
        }
    };

    return (
        <>
            {(audioRequestError || error) && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" onClick={() => retrySelectedAudio()}>
                            Retry
                        </Button>
                    }
                >
                    {audioRequestError || error}
                </Alert>
            )}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '290px minmax(0, 1fr)', xl: '330px minmax(0, 1fr)' },
                    gap: 2.5,
                    alignItems: 'start',
                    fontFamily: INTER,
                }}
            >
                <Box data-testid="pronunciation-sentence-list" sx={{ ...pronunciationCardSx, overflow: 'hidden', minHeight: { md: 650 } }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2.25, borderBottom: `1px solid ${BORDER}` }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography component="h2" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>
                                Prior sentences
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: SLATE }}>
                                {sentences.length} {sentences.length === 1 ? 'sentence' : 'sentences'}
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="Add a new sentence"
                            disabled={busy}
                            onClick={openAdd}
                            sx={{ width: 44, height: 44, borderRadius: '12px', color: BLUE, backgroundColor: SOFT_BLUE }}
                        >
                            <AddRoundedIcon />
                        </IconButton>
                    </Stack>
                    <Box sx={{ maxHeight: { xs: 300, md: 580 }, overflowY: 'auto', p: 1.25 }}>
                        {loading ? (
                            <Stack alignItems="center" sx={{ p: 3 }}>
                                <CircularProgress size={24} aria-label="Loading sentences" />
                            </Stack>
                        ) : !sentences.length ? (
                            <Typography sx={{ p: 1.25, fontSize: 13, color: SLATE }}>
                                No sentences yet. Add one to start practicing.
                            </Typography>
                        ) : (
                            ['Today', 'Yesterday', 'Older'].map((group) => {
                                const items = sentences.filter((sentence) => sentenceGroup(sentence) === group);
                                return items.length ? (
                                    <Box key={group} sx={{ mb: 1.5 }}>
                                        <Typography
                                            sx={{
                                                px: 1.25,
                                                py: 0.75,
                                                fontWeight: 700,
                                                fontSize: 11,
                                                letterSpacing: '.07em',
                                                color: SLATE,
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {group}
                                        </Typography>
                                        <Stack spacing={0.5}>
                                            {items.map((sentence) => {
                                                const ready = sentenceIsPlayable(sentence, teacher);
                                                const failed = !ready && Boolean(sentence.processing_error) &&
                                                    !sentence.audio_request_status?.[teacher];
                                                const delayed = !ready && sentence.audio_request_status?.[teacher]?.state === 'retrying';
                                                const queued = !ready && !delayed && ((sentence.processing_queue ?? 0) > 0 ||
                                                    sentence.audio_request_status?.[teacher]?.state === 'queued');
                                                const summary =
                                                    sentence.recording_count !== undefined
                                                        ? { count: sentence.recording_count, score: sentence.latest_score ?? null }
                                                        : summaries[sentence.id];
                                                return (
                                                    <ButtonBase
                                                        key={sentence.id}
                                                        disabled={busy}
                                                        aria-pressed={sentence.id === selectedId}
                                                        onClick={() => setSelectedId(sentence.id)}
                                                        sx={{
                                                            width: '100%',
                                                            p: 1.35,
                                                            textAlign: 'left',
                                                            borderRadius: '13px',
                                                            backgroundColor: sentence.id === selectedId ? SOFT_BLUE : 'transparent',
                                                            '&:hover': { backgroundColor: SURFACE_MUTED },
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ width: '100%' }}>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography
                                                                    noWrap
                                                                    sx={{
                                                                        fontFamily: POPPINS,
                                                                        fontWeight: 600,
                                                                        fontSize: 13.5,
                                                                        color: NAVY,
                                                                    }}
                                                                >
                                                                    {sentence.text}
                                                                </Typography>
                                                                <Typography noWrap sx={{ fontSize: 11.5, color: failed ? ERROR_TEXT : SLATE, mt: 0.2 }}>
                                                                    {failed
                                                                        ? 'Audio preparation failed'
                                                                        : delayed
                                                                          ? 'Audio delayed · retry scheduled'
                                                                        : queued
                                                                          ? 'Preparing model audio'
                                                                          : ready && (sentence.processing_queue ?? 0) > 0
                                                                            ? 'Ready to practice · other voices preparing'
                                                                          : ready
                                                                            ? summary
                                                                                ? `${summary.count} saved recording${summary.count === 1 ? '' : 's'}`
                                                                                : 'Ready to practice'
                                                                            : 'Waiting for model audio'}
                                                                </Typography>
                                                            </Box>
                                                            {failed ? (
                                                                <Chip
                                                                    icon={<ErrorOutlineRoundedIcon />}
                                                                    label="Error"
                                                                    color="error"
                                                                    size="small"
                                                                    sx={{ height: 24, fontSize: 11, fontWeight: 700, flexShrink: 0 }}
                                                                />
                                                            ) : delayed ? (
                                                                <Chip label="Delayed" color="warning" size="small"
                                                                    sx={{ height: 24, fontSize: 11, fontWeight: 700, flexShrink: 0 }} />
                                                            ) : queued ? (
                                                                <Chip
                                                                    icon={<HourglassTopRoundedIcon />}
                                                                    label={(sentence.processing_queue ?? 0) > 0 ? `Position ${sentence.processing_queue}` : 'Queued'}
                                                                    size="small"
                                                                    sx={{ ...queueChipSx, height: 24, fontSize: 11, flexShrink: 0 }}
                                                                />
                                                            ) : ready && summary?.score != null ? (
                                                                <Typography sx={{ fontWeight: 700, fontSize: 11.5, color: NAVY }}>
                                                                    {Math.round(summary.score)}%
                                                                </Typography>
                                                            ) : null}
                                                        </Stack>
                                                    </ButtonBase>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                ) : null;
                            })
                        )}
                    </Box>
                </Box>
                <Box
                    data-testid="pronunciation-practice-card"
                    sx={{ ...pronunciationCardSx, p: { xs: 2.25, lg: 3.5 }, minHeight: { xs: 420, md: 650 }, minWidth: 0 }}
                >
                    {selected ? (
                        <>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography sx={{ flex: 1, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: SLATE }}>
                                    {selectedReady
                                        ? 'PRONUNCIATION PRACTICE'
                                        : selectedAudioFailed
                                        ? 'SENTENCE AUDIO'
                                        : selectedAudioDelayed
                                        ? 'AUDIO DELAYED'
                                        : 'PROCESSING SENTENCE'}
                                </Typography>
                                <IconButton
                                    aria-label="Delete sentence"
                                    disabled={busy}
                                    onClick={() => {
                                        setDeleteError('');
                                        setDeleteSentence(selected);
                                    }}
                                    sx={{ color: SLATE }}
                                >
                                    <DeleteOutlineRoundedIcon />
                                </IconButton>
                            </Stack>
                            {!selectedReady ? (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    spacing={2}
                                    sx={{ minHeight: { xs: 320, md: 520 }, textAlign: 'center' }}
                                >
                                    <Box
                                        sx={{
                                            width: 72,
                                            height: 72,
                                            borderRadius: '50%',
                                            display: 'grid',
                                            placeItems: 'center',
                                            color: selectedAudioFailed ? ERROR_TEXT : BLUE,
                                            backgroundColor: selectedAudioFailed ? ERROR_SOFT : SOFT_BLUE,
                                        }}
                                    >
                                        {selectedAudioFailed
                                            ? <ErrorOutlineRoundedIcon sx={{ fontSize: 34 }} />
                                            : <HourglassTopRoundedIcon sx={{ fontSize: 34 }} />}
                                    </Box>
                                    <Typography component="h3" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 23, color: NAVY }}>
                                        {selectedAudioFailed ? 'Audio preparation error'
                                            : selectedAudioDelayed ? 'Audio preparation delayed' : 'Preparing your sentence'}
                                    </Typography>
                                    <Typography sx={{ color: NAVY, overflowWrap: 'anywhere' }}>{selected.text}</Typography>
                                    <Typography sx={{ maxWidth: 480, fontSize: 14, lineHeight: 1.6, color: SLATE }}>
                                        {selectedAudioFailed
                                            ? 'Audio preparation failed. You can retry now. If it fails again, contact your teacher or support.'
                                            : selectedAudioDelayed
                                            ? 'Audio generation hit a delay. The queue will retry automatically, or you can retry it now.'
                                            : 'We are preparing your selected teacher voice. This page checks for updates while you wait; you can practice as soon as that voice is ready.'}
                                    </Typography>
                                    {(selectedAudioFailed || selectedAudioDelayed) && <Button onClick={() => retrySelectedAudio(true)}>Retry audio</Button>}
                                    {!selectedAudioFailed && !selectedAudioDelayed &&
                                        ((selected.processing_queue ?? 0) > 0 || selected.audio_request_status?.[teacher]?.state === 'queued') && (
                                        <Chip label={(selected.processing_queue ?? 0) > 0
                                            ? `Queue position: ${selected.processing_queue}` : 'Queued'} sx={queueChipSx} />
                                    )}
                                </Stack>
                            ) : (
                                <StudentPronunciationPractice
                                    key={selected.id}
                                    sentence={selected}
                                    onBusy={setBusy}
                                    onSummary={updateSummary}
                                    onSaved={refresh}
                                />
                            )}
                        </>
                    ) : (
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            spacing={1.5}
                            sx={{ minHeight: { xs: 320, md: 570 }, textAlign: 'center' }}
                        >
                            <Box
                                sx={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: '18px',
                                    display: 'grid',
                                    placeItems: 'center',
                                    color: BLUE,
                                    backgroundColor: SOFT_BLUE,
                                }}
                            >
                                <AddRoundedIcon sx={{ fontSize: 30 }} />
                            </Box>
                            <Typography component="h2" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY }}>
                                {loading ? 'Loading your sentences' : 'No practice sentences yet'}
                            </Typography>
                            {!loading && (
                                <>
                                    <Typography sx={{ maxWidth: 420, fontSize: 13.5, lineHeight: 1.6, color: SLATE }}>
                                        Add a Chinese sentence to generate pinyin, model audio, and recording feedback.
                                    </Typography>
                                    <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openAdd} sx={pronunciationButtonSx}>
                                        Add a sentence
                                    </Button>
                                </>
                            )}
                        </Stack>
                    )}
                </Box>
            </Box>
            <Dialog
                open={addOpen}
                onClose={closeAdd}
                fullWidth
                maxWidth="sm"
                PaperProps={{ component: 'form', onSubmit: addSentence, sx: { borderRadius: '18px' } }}
            >
                <DialogTitle sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>Add a new sentence</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, color: SLATE, mb: 2 }}>
                        Type a Chinese sentence. We will generate pinyin and prepare model audio for you.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        label="Sentence"
                        value={draft}
                        disabled={adding}
                        onChange={(event) => {
                            setDraft(event.target.value);
                            setAddError('');
                        }}
                        error={Boolean(addError)}
                        helperText={addError || 'Up to 70 characters. A verified email is required.'}
                    />
                    {adding && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                            <CircularProgress size={18} />
                            <Typography role="status" sx={{ color: SLATE, fontSize: 13 }}>
                                Adding your sentence…
                            </Typography>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeAdd} disabled={adding}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={adding || !draft.trim()} sx={pronunciationButtonSx}>
                        Add sentence
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={Boolean(deleteSentence)}
                onClose={() => {
                    if (!deleting) setDeleteSentence(null);
                }}
                PaperProps={{ sx: { borderRadius: '18px' } }}
            >
                <DialogTitle sx={{ fontFamily: POPPINS, color: NAVY }}>Delete sentence?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: SLATE }}>Remove this sentence from your practice list?</Typography>
                    <Typography sx={{ mt: 1, color: NAVY }}>{deleteSentence?.text}</Typography>
                    {deleteError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {deleteError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button disabled={deleting} onClick={() => setDeleteSentence(null)}>
                        Cancel
                    </Button>
                    <Button disabled={deleting} onClick={() => void confirmDelete()} sx={{ color: ERROR_TEXT }}>
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
